import { useEffect, useRef } from 'react';

const path = process.env.REACT_APP_STATIC_PATH || '';

/**
 * A hook that runs a callback at a specified interval using a Web Worker.
 * This ensures the interval continues to run reliably even when the tab is in the background
 * or throttled by the browser, unlike regular setInterval which can be paused or delayed.
 *
 * @param callback - The function to execute at each interval
 * @param delay - The interval delay in milliseconds
 */
export const useBackgroundInterval = (callback: () => void, delay: number | null) => {
  const savedCallback = useRef<() => void>();
  const workerRef = useRef<Worker | null>(null);

  // Remember the latest callback
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the Web Worker interval
  useEffect(() => {
    if (delay === null) {
      return undefined;
    }

    // Create a new Web Worker
    let worker: Worker | null = null;
    let handleMessage: ((event: MessageEvent) => void) | null = null;

    try {
      worker = new Worker(`${path}/intervalWorker.js`);
      workerRef.current = worker;

      // Listen for messages from the worker
      handleMessage = (event: MessageEvent) => {
        if (event.data.type === 'TICK') {
          // Execute the callback on each tick
          savedCallback.current?.();
        }
      };

      worker.addEventListener('message', handleMessage);

      // Start the worker interval
      worker.postMessage({ type: 'START', delay });
    } catch (error) {
      // Clean up partially initialized worker if it was created
      if (worker) {
        if (handleMessage) {
          worker.removeEventListener('message', handleMessage);
        }
        worker.terminate();
        workerRef.current = null;
      }

      // Fallback to regular setInterval if Web Worker is not available
      // eslint-disable-next-line no-console
      console.warn('Web Worker not available, falling back to setInterval:', error);

      const intervalId = setInterval(() => {
        savedCallback.current?.();
      }, delay);

      return () => {
        clearInterval(intervalId);
      };
    }

    // Cleanup function - use local worker reference to ensure correct instance is cleaned up
    return () => {
      if (worker) {
        if (handleMessage) {
          worker.removeEventListener('message', handleMessage);
        }
        worker.postMessage({ type: 'STOP' });
        worker.terminate();
        if (workerRef.current === worker) {
          workerRef.current = null;
        }
      }
    };
  }, [delay]);
};
